/**
 * API service with type-safe backend integration
 *
 * This service ensures type alignment between frontend and backend by:
 * 1. Using types derived from backend models
 * 2. Runtime validation of API responses
 * 3. Proper error handling and type guards
 */

import {
  Project,
  ListProjectsResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  DuplicateProjectRequest,
  DuplicateProjectResponse,
  Document,
  Column,
  CreateColumnRequest,
  UpdateColumnRequest,
  ListColumnsResponse,
  GeneratePromptRequest,
  GeneratePromptResponse,
  UploadDocumentsRequest,
  UploadDocumentsResponse,
  ListDocumentsResponse,
  DeleteDocumentsResponse,
  Template,
  TemplateColumn,
  ProjectMember,
  TemplateMember,
  AddMemberRequest,
  UpdateMemberRequest,
  ListMembersResponse,
  ApiError,
  isApiError,
  ListProjectMembersResponse,
  ListTemplateMembersResponse,
  AddProjectMemberRequest,
  AddTemplateMemberRequest,
  UpdateProjectMemberRequest,
  MemberOperationResponse,
  // New analysis types
  AnalysisOverviewResponse,
  AnalysisRun,
  StartAnalysisRequest,
  StartAnalysisResponse,
  CancelAnalysisResponse,
  AnalysisMode,
  BatchFeedbackRequest,
  BatchFeedbackResponse,
  ResultsResponse,
  // People API types
  PeopleSearchResponse,
  PeopleSearchSuggestion,
  PeopleValidateResponse,
  // Document Space types
  DocumentSpace,
  SpaceMember,
  CreateSpaceRequest,
  UpdateSpaceRequest,
  SpaceUploadDocumentsResponse,
  ListSpaceDocumentsResponse,
  DeleteSpaceDocumentsResponse,
  SpaceCopyJobResponse,
  // Document Group types
  DocumentGroup,
  CreateDocumentGroupRequest,
  UpdateDocumentGroupRequest,
  ListDocumentGroupsResponse,
} from '../types';
import {
  validateProject,
  validateProjectArray,
  validateListProjectsResponse,
  validateColumn,
  ValidationError,
  debugTypeAlignment,
} from '../utils/validation';
import { sentryApiRequest } from '../utils/api';

// ============================================================================
// API SERVICE CLASS
// ============================================================================

export class ApiService {
  // ========================================================================
  // PROJECT OPERATIONS
  // ========================================================================

  /**
   * List projects for the current user with type validation
   */
  async listProjects(params?: {
    status?: string;
    search?: string;
    maxResults?: number;
  }): Promise<ListProjectsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.maxResults) searchParams.append('maxResults', params.maxResults.toString());

    const endpoint = `/projects${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    try {
      const response = await sentryApiRequest<any>(endpoint);

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'ListProjectsResponse');
      }

      // Validate response structure
      return validateListProjectsResponse(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);
        console.error('Field:', error.field, 'Value:', error.value);

        // In development, show detailed error
        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in listProjects: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Create a new project with type validation
   */
  async createProject(data: CreateProjectRequest): Promise<Project> {
    try {
      const response = await sentryApiRequest<any>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'Project');
      }

      // Validate response structure
      return validateProject(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in createProject: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Get project details with type validation
   */
  async getProject(projectId: string, includeDetails: boolean = true): Promise<Project> {
    const params = new URLSearchParams();
    if (includeDetails) {
      params.append('includeDocumentCount', 'true');
      params.append('includeColumnCount', 'true');
    }

    const endpoint = `/projects/${projectId}${params.toString() ? `?${params.toString()}` : ''}`;

    try {
      const response = await sentryApiRequest<any>(endpoint);

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'Project');
      }

      // Validate response structure
      return validateProject(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in getProject: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Update project with type validation
   */
  async updateProject(projectId: string, data: UpdateProjectRequest): Promise<Project> {
    try {
      const response = await sentryApiRequest<any>(`/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      return validateProject(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in updateProject: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Delete a project
   */
  async deleteProject(projectId: string): Promise<{ success: boolean }> {
    return sentryApiRequest<{ success: boolean }>(`/projects/${projectId}?confirm=true`, {
      method: 'DELETE',
    });
  }

  /**
   * Duplicate a project with type validation
   */
  async duplicateProject(projectId: string, data: DuplicateProjectRequest): Promise<DuplicateProjectResponse> {
    try {
      const response = await sentryApiRequest<any>(`/projects/${projectId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify(data),
      });

      // Validate the project in the response
      if (response.project) {
        response.project = validateProject(response.project);
      }

      return response as DuplicateProjectResponse;
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in duplicateProject: ${error.message}`);
        }
      }
      throw error;
    }
  }

  // ========================================================================
  // DOCUMENT OPERATIONS
  // ========================================================================

  /**
   * Upload documents to a project
   */
  async uploadDocuments(projectId: string, files: File[]): Promise<UploadDocumentsResponse> {
    // Map file extensions to MIME types for fallback when browser doesn't detect type
    const extensionToMimeType: Record<string, string> = {
      '.pdf': 'application/pdf',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.txt': 'text/plain',
      '.csv': 'text/csv',
      '.md': 'text/markdown',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.tiff': 'image/tiff',
      '.tif': 'image/tiff',
      '.bmp': 'image/bmp',
      '.msg': 'application/vnd.ms-outlook',
    };

    const getContentType = (file: File): string => {
      if (file.type) return file.type;
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return extensionToMimeType[ext] || 'application/octet-stream';
    };

    try {
      // Prepare file metadata
      const filesMetadata = files.map((file) => ({
        fileName: file.name,
        fileSize: file.size,
        contentType: getContentType(file),
      }));

      // Get presigned URLs from backend
      const response = await sentryApiRequest<UploadDocumentsResponse>(`/projects/${projectId}/documents`, {
        method: 'POST',
        body: JSON.stringify({ files: filesMetadata }),
      });

      // Upload files directly to S3 using presigned URLs
      const uploadPromises = response.uploadUrls.map(async (uploadData, index) => {
        const file = files[index];
        const formData = new FormData();

        // Add all required fields from presigned URL
        Object.entries(uploadData.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });

        // Add the file last (required by S3)
        formData.append('file', file);

        // Upload directly to S3
        const uploadResponse = await fetch(uploadData.uploadUrl, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed for ${file.name}: ${uploadResponse.statusText}`);
        }

        return uploadData.documentId;
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      return response;
    } catch (error) {
      console.error('Document upload failed:', error);
      throw error;
    }
  }

  /**
   * List documents for a project
   */
  async listDocuments(
    projectId: string,
    params?: {
      status?: string;
      limit?: number;
    },
  ): Promise<ListDocumentsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const endpoint = `/projects/${projectId}/documents${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return sentryApiRequest<ListDocumentsResponse>(endpoint);
  }

  /**
   * Delete documents from a project
   */
  async deleteDocuments(projectId: string, documentIds: string[]): Promise<DeleteDocumentsResponse> {
    const endpoint = `/projects/${projectId}/documents?documentIds=${documentIds.join(',')}`;

    return sentryApiRequest<DeleteDocumentsResponse>(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Get document details
   */
  async getDocument(projectId: string, documentId: string): Promise<Document> {
    return sentryApiRequest<Document>(`/projects/${projectId}/documents/${documentId}`);
  }

  /**
   * Get presigned URL for downloading original document
   */
  async getDocumentDownloadUrl(
    projectId: string,
    documentId: string,
  ): Promise<{
    documentId: string;
    url: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    expiresIn: number;
  }> {
    return sentryApiRequest<{
      documentId: string;
      url: string;
      fileName: string;
      fileSize: number;
      fileType: string;
      expiresIn: number;
    }>(`/projects/${projectId}/documents/${documentId}/download`);
  }

  /**
   * Get presigned URL for downloading extracted text
   */
  async getExtractedTextDownloadUrl(
    projectId: string,
    documentId: string,
  ): Promise<{
    documentId: string;
    url: string;
    fileName: string;
    expiresIn: number;
  }> {
    return sentryApiRequest<{
      documentId: string;
      url: string;
      fileName: string;
      expiresIn: number;
    }>(`/projects/${projectId}/documents/${documentId}/download-extracted`);
  }

  // ========================================================================
  // COLUMN OPERATIONS
  // ========================================================================

  /**
   * List columns for a project with type validation
   */
  async listColumns(
    projectId: string,
    params?: {
      activeOnly?: boolean;
    },
  ): Promise<Column[]> {
    const searchParams = new URLSearchParams();
    if (params?.activeOnly !== undefined) {
      searchParams.append('activeOnly', params.activeOnly.toString());
    }

    const endpoint = `/projects/${projectId}/columns${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    try {
      const response = await sentryApiRequest<ListColumnsResponse>(endpoint);

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'ListColumnsResponse');
      }

      // Validate response structure and return columns array
      if (!response.columns || !Array.isArray(response.columns)) {
        throw new ValidationError('Invalid response: columns array missing', 'columns', response.columns);
      }

      // Validate each column
      const validatedColumns = response.columns.map((column, index) => {
        try {
          return validateColumn(column);
        } catch (error) {
          if (error instanceof ValidationError) {
            throw new ValidationError(
              `Column ${index} validation failed: ${error.message}`,
              `columns[${index}].${error.field}`,
              error.value,
            );
          }
          throw error;
        }
      });

      return validatedColumns;
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in listColumns: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Alias for listColumns for backward compatibility
   */
  async getColumns(projectId: string, params?: { activeOnly?: boolean }): Promise<Column[]> {
    return this.listColumns(projectId, params);
  }

  /**
   * Alias for listDocuments for backward compatibility
   */
  async getDocuments(
    projectId: string,
    page?: number,
    limit?: number,
    forceRefresh?: boolean,
  ): Promise<ListDocumentsResponse> {
    return this.listDocuments(projectId, { limit });
  }

  /**
   * Create a new column with type validation
   */
  async createColumn(projectId: string, data: CreateColumnRequest): Promise<Column> {
    try {
      const response = await sentryApiRequest<any>(`/projects/${projectId}/columns`, {
        method: 'POST',
        body: JSON.stringify(data),
      });

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'Column');
      }

      // Validate response structure
      return validateColumn(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in createColumn: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Get a specific column with type validation
   */
  async getColumn(projectId: string, columnId: string): Promise<Column> {
    try {
      const response = await sentryApiRequest<any>(`/projects/${projectId}/columns/${columnId}`);

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'Column');
      }

      // Validate response structure
      return validateColumn(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in getColumn: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Update a column with type validation
   */
  async updateColumn(projectId: string, columnId: string, data: UpdateColumnRequest): Promise<Column> {
    try {
      const response = await sentryApiRequest<any>(`/projects/${projectId}/columns/${columnId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      // Debug type alignment in development
      if (process.env.NODE_ENV === 'development') {
        debugTypeAlignment(response, 'Column');
      }

      // Validate response structure
      return validateColumn(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error('API Response validation failed:', error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in updateColumn: ${error.message}`);
        }
      }
      throw error;
    }
  }

  /**
   * Delete a column (soft delete)
   */
  async deleteColumn(projectId: string, columnId: string): Promise<void> {
    await sentryApiRequest<{ success: boolean }>(`/projects/${projectId}/columns/${columnId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Ask the backend model to write (or, with `draftPrompt`, improve) a column's
   * analysis instructions.
   */
  async generatePrompt(data: GeneratePromptRequest): Promise<GeneratePromptResponse> {
    return sentryApiRequest<GeneratePromptResponse>('/generate-prompt', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ========================================================================
  // TEMPLATE OPERATIONS
  // ========================================================================

  /**
   * Get templates for the current user (includes user's own and shared templates)
   */
  async getTemplates(): Promise<Template[]> {
    try {
      const response = await sentryApiRequest<Template[] | { templates: Template[] }>('/templates');
      // Backend returns array directly, but handle wrapped format for compatibility
      if (Array.isArray(response)) {
        return response;
      }
      return response.templates || [];
    } catch (error) {
      console.error('Failed to get templates:', error);
      throw error;
    }
  }

  /**
   * Get a single template by ID
   */
  async getTemplate(templateId: string): Promise<Template> {
    try {
      const response = await sentryApiRequest<Template>(`/templates/${templateId}`);
      return response;
    } catch (error) {
      console.error('Failed to get template:', error);
      throw error;
    }
  }

  /**
   * Save current project columns as a template
   */
  async saveTemplate(data: {
    name: string;
    description?: string;
    visibility: 'user' | 'shared';
    columns: TemplateColumn[];
  }): Promise<Template> {
    try {
      const response = await sentryApiRequest<Template>('/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      console.error('Failed to save template:', error);
      throw error;
    }
  }

  /**
   * Delete a template (owner only)
   */
  async deleteTemplate(templateId: string): Promise<{ success: boolean }> {
    try {
      const response = await sentryApiRequest<{ success: boolean; templateId: string }>(`/templates/${templateId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw error;
    }
  }

  // ========================================================================
  // PROJECT MEMBER OPERATIONS (Sharing)
  // ========================================================================

  /**
   * List members of a project
   */
  async listProjectMembers(projectId: string): Promise<{ members: ProjectMember[]; totalCount: number }> {
    try {
      const response = await sentryApiRequest<{ members: ProjectMember[]; totalCount: number }>(
        `/projects/${projectId}/members`,
      );
      return response;
    } catch (error) {
      console.error('Failed to list project members:', error);
      throw error;
    }
  }

  /**
   * Add a member to a project
   */
  async addProjectMember(projectId: string, data: AddMemberRequest): Promise<ProjectMember> {
    try {
      const response = await sentryApiRequest<ProjectMember>(`/projects/${projectId}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      console.error('Failed to add project member:', error);
      throw error;
    }
  }

  /**
   * Update a project member's role
   */
  async updateProjectMember(projectId: string, memberId: string, data: UpdateMemberRequest): Promise<ProjectMember> {
    try {
      const response = await sentryApiRequest<ProjectMember>(
        `/projects/${projectId}/members/${encodeURIComponent(memberId)}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        },
      );
      return response;
    } catch (error) {
      console.error('Failed to update project member:', error);
      throw error;
    }
  }

  /**
   * Remove a member from a project
   */
  async removeProjectMember(projectId: string, memberId: string): Promise<{ success: boolean }> {
    try {
      const response = await sentryApiRequest<{ success: boolean; memberId: string }>(
        `/projects/${projectId}/members/${encodeURIComponent(memberId)}`,
        { method: 'DELETE' },
      );
      return response;
    } catch (error) {
      console.error('Failed to remove project member:', error);
      throw error;
    }
  }

  // ========================================================================
  // TEMPLATE MEMBER OPERATIONS (Sharing)
  // ========================================================================

  /**
   * List members of a template
   */
  async listTemplateMembers(templateId: string): Promise<{ members: TemplateMember[]; totalCount: number }> {
    try {
      const response = await sentryApiRequest<{ members: TemplateMember[]; totalCount: number }>(
        `/templates/${templateId}/members`,
      );
      return response;
    } catch (error) {
      console.error('Failed to list template members:', error);
      throw error;
    }
  }

  /**
   * Add a member to a template (viewer only)
   */
  async addTemplateMember(
    templateId: string,
    data: { memberName: string; memberType?: string },
  ): Promise<TemplateMember> {
    try {
      const response = await sentryApiRequest<TemplateMember>(`/templates/${templateId}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      console.error('Failed to add template member:', error);
      throw error;
    }
  }

  /**
   * Remove a member from a template
   */
  async removeTemplateMember(templateId: string, memberId: string): Promise<{ success: boolean }> {
    try {
      const response = await sentryApiRequest<{ success: boolean; memberId: string }>(
        `/templates/${templateId}/members/${encodeURIComponent(memberId)}`,
        { method: 'DELETE' },
      );
      return response;
    } catch (error) {
      console.error('Failed to remove template member:', error);
      throw error;
    }
  }

  // ========================================================================
  // ANALYSIS OPERATIONS - New Consolidated RESTful API
  // ========================================================================

  /**
   * Get analysis overview for a project
   * Returns comprehensive status, history, and statistics
   * Endpoint: GET /projects/{projectId}/analysis
   */
  async getAnalysisOverview(
    projectId: string,
    params?: {
      includeStatistics?: boolean;
      limit?: number;
      offset?: number;
    },
  ): Promise<AnalysisOverviewResponse> {
    const searchParams = new URLSearchParams();
    if (params?.includeStatistics !== undefined) {
      searchParams.append('includeStatistics', params.includeStatistics.toString());
    }
    if (params?.limit !== undefined) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      searchParams.append('offset', params.offset.toString());
    }

    const endpoint = `/projects/${projectId}/analysis${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    const response = await sentryApiRequest<AnalysisOverviewResponse>(endpoint);
    console.log('[API] Analysis overview:', response);
    return response;
  }

  /**
   * Get latest analysis run for a project (lightweight)
   * Endpoint: GET /projects/{projectId}/analysis/latest
   */
  async getLatestAnalysisRun(projectId: string): Promise<AnalysisRun | null> {
    try {
      const response = await sentryApiRequest<AnalysisRun>(`/projects/${projectId}/analysis/latest`);
      console.log('[API] Latest analysis run:', response);
      return response;
    } catch (error: any) {
      // Return null if no analysis runs exist (404)
      if (error?.status === 404 || error?.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get specific analysis run by ID
   * Endpoint: GET /projects/{projectId}/analysis/{analysisRunId}
   * Note: analysisRunId is URL-encoded because it contains special characters (#, :, +)
   */
  async getAnalysisRun(projectId: string, analysisRunId: string): Promise<AnalysisRun> {
    const response = await sentryApiRequest<AnalysisRun>(
      `/projects/${projectId}/analysis/${encodeURIComponent(analysisRunId)}`,
    );
    console.log('[API] Analysis run:', response);
    return response;
  }

  /**
   * Start analysis for a project
   * Endpoint: POST /projects/{projectId}/analysis
   */
  async startAnalysis(projectId: string, options?: StartAnalysisRequest): Promise<StartAnalysisResponse> {
    const requestBody: StartAnalysisRequest = {
      analysisMode: options?.analysisMode || 'full',
      documentIds: options?.documentIds,
      columnIds: options?.columnIds,
      groupIds: options?.groupIds,
      modelConfig: options?.modelConfig,
      estimatedCost: options?.estimatedCost,
      notes: options?.notes,
    };

    // Remove undefined values
    Object.keys(requestBody).forEach((key) => {
      if (requestBody[key as keyof StartAnalysisRequest] === undefined) {
        delete requestBody[key as keyof StartAnalysisRequest];
      }
    });

    console.log('[API] Starting analysis with request body:', JSON.stringify(requestBody, null, 2));

    try {
      const response = await sentryApiRequest<StartAnalysisResponse>(`/projects/${projectId}/analysis`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      console.log('[API] Analysis started successfully:', response);
      return response;
    } catch (error) {
      console.error('[API] Analysis start failed:', error);
      if (error instanceof Error) {
        console.error('[API] Error message:', error.message);
      }
      throw error;
    }
  }

  /**
   * Cancel an analysis run
   * Endpoint: DELETE /projects/{projectId}/analysis/{analysisRunId}
   * Note: analysisRunId is URL-encoded because it contains special characters (#, :, +)
   */
  async cancelAnalysis(projectId: string, analysisRunId: string): Promise<CancelAnalysisResponse> {
    console.log(`[API] Cancelling analysis run ${analysisRunId} for project ${projectId}`);

    const response = await sentryApiRequest<CancelAnalysisResponse>(
      `/projects/${projectId}/analysis/${encodeURIComponent(analysisRunId)}`,
      { method: 'DELETE' },
    );

    console.log('[API] Analysis cancelled:', response);
    return response;
  }

  /**
   * Get analysis status for a project (legacy - for backward compatibility)
   * Maps to new getAnalysisOverview or getLatestAnalysisRun based on parameters
   * @deprecated Use getAnalysisOverview or getLatestAnalysisRun instead
   */
  async getAnalysisStatus(
    projectId: string,
    analysisRunId?: string,
  ): Promise<{
    status: 'not_started' | 'extracting_text' | 'analyzing' | 'completed' | 'failed';
    phase?: 'text_extraction' | 'bedrock_analysis';
    totalDocuments: number;
    processedDocuments: number;
    totalColumns: number;
    processedResults: number;
    totalExpectedResults: number;
    progressPercentage: number;
    startedAt?: string;
    analysisRunId?: string;
    executionArn?: string;
    executionStatus?: string;
    executionStartDate?: string;
    executionStopDate?: string;
  }> {
    try {
      // If analysisRunId is provided, get specific run
      if (analysisRunId) {
        const run = await this.getAnalysisRun(projectId, analysisRunId);
        return this.mapAnalysisRunToLegacyStatus(run);
      }

      // Otherwise, get latest run from overview
      const overview = await this.getAnalysisOverview(projectId);

      // If there are runs, use the latest one
      if (overview.runs && overview.runs.length > 0) {
        const latestRun = overview.runs[0];
        return this.mapAnalysisRunToLegacyStatus(latestRun);
      }

      // No runs - return not_started status
      return {
        status: 'not_started',
        totalDocuments: overview.totalDocuments,
        processedDocuments: 0,
        totalColumns: overview.totalColumns,
        processedResults: 0,
        totalExpectedResults: overview.totalDocuments * overview.totalColumns,
        progressPercentage: 0,
      };
    } catch (error) {
      console.error('[API] Failed to get analysis status:', error);
      throw error;
    }
  }

  /**
   * Map AnalysisRun to legacy status format
   */
  private mapAnalysisRunToLegacyStatus(run: AnalysisRun): {
    status: 'not_started' | 'extracting_text' | 'analyzing' | 'completed' | 'failed';
    phase?: 'text_extraction' | 'bedrock_analysis';
    totalDocuments: number;
    processedDocuments: number;
    totalColumns: number;
    processedResults: number;
    totalExpectedResults: number;
    progressPercentage: number;
    startedAt?: string;
    analysisRunId?: string;
    executionArn?: string;
    executionStatus?: string;
    executionStartDate?: string;
    executionStopDate?: string;
  } {
    // Map new status to legacy status
    let legacyStatus: 'not_started' | 'extracting_text' | 'analyzing' | 'completed' | 'failed';
    let phase: 'text_extraction' | 'bedrock_analysis' | undefined;

    switch (run.status) {
      case 'running':
        // Determine phase based on progress
        if (run.processedResults === 0) {
          legacyStatus = 'extracting_text';
          phase = 'text_extraction';
        } else {
          legacyStatus = 'analyzing';
          phase = 'bedrock_analysis';
        }
        break;
      case 'completed':
        legacyStatus = 'completed';
        break;
      case 'failed':
        legacyStatus = 'failed';
        break;
      case 'cancelled':
        legacyStatus = 'failed'; // Map cancelled to failed for legacy compatibility
        break;
      default:
        legacyStatus = 'analyzing';
    }

    return {
      status: legacyStatus,
      phase,
      totalDocuments: run.totalDocuments,
      processedDocuments: run.processedDocuments,
      totalColumns: run.totalColumns,
      processedResults: run.processedResults,
      totalExpectedResults: run.totalExpectedResults,
      progressPercentage: run.progressPercentage || 0,
      startedAt: run.startedAt,
      analysisRunId: run.analysisRunId,
      executionArn: run.executionArn,
      executionStatus:
        run.status === 'running'
          ? 'RUNNING'
          : run.status === 'completed'
          ? 'SUCCEEDED'
          : run.status === 'failed'
          ? 'FAILED'
          : 'UNKNOWN',
      executionStartDate: run.startedAt,
      executionStopDate: run.completedAt,
    };
  }

  /**
   * Check if results already exist (legacy - for backward compatibility)
   * @deprecated Use getAnalysisOverview instead
   */
  async checkExistingResults(projectId: string): Promise<{
    hasExistingResults: boolean;
    resultCount: number;
    lastAnalyzedAt?: string;
  }> {
    try {
      const overview = await this.getAnalysisOverview(projectId);

      // Check if there are any completed runs
      const completedRuns = overview.runs?.filter((r) => r.status === 'completed') || [];
      const hasResults = completedRuns.length > 0;
      const lastRun = completedRuns[0];

      return {
        hasExistingResults: hasResults,
        resultCount: lastRun?.processedResults || 0,
        lastAnalyzedAt: lastRun?.completedAt,
      };
    } catch (error) {
      console.error('[API] Failed to check existing results:', error);
      return {
        hasExistingResults: false,
        resultCount: 0,
      };
    }
  }

  // ========================================================================
  // EXPORT OPERATIONS
  // ========================================================================

  /**
   * Export project results to CSV
   */
  async exportResults(
    projectId: string,
    format: 'csv' | 'excel' | 'json' = 'csv',
    columnIds?: string[],
    includeJustifications: boolean = false,
    includeConfidence: boolean = false,
    includeVerbatim: boolean = false,
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (columnIds && columnIds.length > 0) {
      params.append('columnIds', columnIds.join(','));
    }
    if (includeJustifications) {
      params.append('includeJustifications', 'true');
    }
    if (includeConfidence) {
      params.append('includeConfidence', 'true');
    }
    if (includeVerbatim) {
      params.append('includeVerbatim', 'true');
    }

    const endpoint = `/projects/${projectId}/export${params.toString() ? `?${params.toString()}` : ''}`;

    // Get the download URL from the API
    const response = await sentryApiRequest<{ downloadUrl: string; expiresAt: number }>(endpoint);

    // Download the file from the presigned URL
    const fileResponse = await fetch(response.downloadUrl);
    if (!fileResponse.ok) {
      throw new Error('Failed to download export file');
    }

    return fileResponse.blob();
  }

  /**
   * Trigger text extraction manually for documents stuck in 'uploaded' status
   */
  async triggerTextExtraction(
    projectId: string,
    documentId: string,
  ): Promise<{
    success: boolean;
    message?: string;
  }> {
    return sentryApiRequest<any>(`/projects/${projectId}/documents/${documentId}/extract-text`, {
      method: 'POST',
    });
  }

  /**
   * Get analysis results for a project
   */
  async getResults(projectId: string): Promise<ResultsResponse> {
    let pageSize = 50;
    const MIN_PAGE_SIZE = 1;
    const maxPages = 200;
    const results: ResultsResponse['results'] = [];
    let columns: Column[] = [];
    let nextToken: string | undefined;

    for (let page = 0; page < maxPages; page += 1) {
      const searchParams = new URLSearchParams({ pageSize: pageSize.toString() });
      if (nextToken) {
        searchParams.set('nextToken', nextToken);
      }

      let response: Partial<ResultsResponse> & {
        hasMore?: boolean;
        nextToken?: string;
      };
      try {
        response = await sentryApiRequest<any>(`/projects/${projectId}/results?${searchParams.toString()}`);
      } catch (error) {
        const requestError = error as Error & {
          status?: number;
          responseBody?: {
            error?: {
              code?: string;
              details?: Partial<ResultsResponse>;
            };
          };
        };
        const message = error instanceof Error ? error.message : '';
        const errorCode = requestError.responseBody?.error?.code;
        const details = requestError.responseBody?.error?.details;
        if (
          requestError.status === 413 ||
          errorCode === 'RESULTS_PAYLOAD_TOO_LARGE' ||
          message.includes('HTTP 413') ||
          message.includes('RESULTS_PAYLOAD_TOO_LARGE')
        ) {
          // Shrink the page and retry the SAME page before giving up, so a
          // wide project that fits at a smaller pageSize still loads instead
          // of dropping straight to the export banner (50 → 10 → 2 → 1).
          if (pageSize > MIN_PAGE_SIZE) {
            pageSize = Math.max(MIN_PAGE_SIZE, Math.floor(pageSize / 5));
            continue;
          }
          // Even a single-document page is over the backend guard — surface the
          // too-large signal (with counts) so the UI shows the export banner.
          console.warn(
            `[Results] Page for project ${projectId} exceeds the size guard even at pageSize=${MIN_PAGE_SIZE}`,
          );
          return {
            results: [],
            columns: [],
            tooLarge: true,
            totalDocuments: details?.totalDocuments,
            totalColumns: details?.totalColumns,
            totalCells: details?.totalCells,
            useExport: true,
            pageSize: details?.pageSize || pageSize,
          };
        }
        throw error;
      }

      if (response.tooLarge) {
        return {
          results: [],
          columns: [],
          tooLarge: true,
          totalDocuments: response.totalDocuments,
          totalColumns: response.totalColumns,
          totalCells: response.totalCells,
          useExport: response.useExport !== false,
          pageSize: response.pageSize || pageSize,
        };
      }

      if (Array.isArray(response.results)) {
        results.push(...response.results);
      }
      if (Array.isArray(response.columns) && (columns.length === 0 || response.columns.length > 0)) {
        columns = response.columns;
      }

      if (!response.hasMore || !response.nextToken) {
        return { results, columns };
      }
      nextToken = response.nextToken;
    }

    console.warn(`[Results] Stopped loading after ${maxPages} pages for project ${projectId}`);
    // truncated: results are partial (the page cap was hit), so callers relying on
    // a complete set — e.g. the destructive-group results-loss warning — must treat
    // coverage as incomplete rather than trusting the partial rows.
    return { results, columns, truncated: true };
  }

  /**
   * Submit or update batch feedback for all columns of a document
   */
  async submitBatchFeedback(
    projectId: string,
    documentId: string,
    data: BatchFeedbackRequest,
  ): Promise<BatchFeedbackResponse> {
    return sentryApiRequest<BatchFeedbackResponse>(`/projects/${projectId}/results/${documentId}/feedback`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Alias for getProjects for backward compatibility
   */
  async getProjects(): Promise<Project[]> {
    const response = await this.listProjects();
    return response.projects;
  }

  // ========================================================================
  // PEOPLE API OPERATIONS
  // ========================================================================

  /**
   * Search Amazon employee aliases via PAPI autocomplete.
   * Returns empty suggestions on error — never throws.
   */
  async searchPeople(query: string): Promise<PeopleSearchResponse> {
    try {
      const params = new URLSearchParams({ q: query });
      return await sentryApiRequest<PeopleSearchResponse>(`/people/search?${params.toString()}`);
    } catch (error) {
      console.error('People search failed:', error);
      return { suggestions: [] };
    }
  }

  /**
   * Validate that an Amazon employee alias exists in PAPI.
   * Throws with the backend error message on HTTP 400 (alias not found).
   * Returns valid=true on success or PAPI unavailability.
   */
  async validateAlias(alias: string): Promise<PeopleValidateResponse> {
    const params = new URLSearchParams({ alias });
    const response = await sentryApiRequest<PeopleValidateResponse>(`/people/validate?${params.toString()}`);
    return response;
  }

  // ========================================================================
  // DOCUMENT SPACE OPERATIONS
  // ========================================================================

  async listSpaces(): Promise<DocumentSpace[]> {
    try {
      const response = await sentryApiRequest<DocumentSpace[] | { spaces: DocumentSpace[] }>('/spaces');
      if (Array.isArray(response)) return response;
      return response.spaces || [];
    } catch (error) {
      console.error('Failed to list spaces:', error);
      throw error;
    }
  }

  async createSpace(data: CreateSpaceRequest): Promise<DocumentSpace> {
    try {
      return await sentryApiRequest<DocumentSpace>('/spaces', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to create space:', error);
      throw error;
    }
  }

  async getSpace(spaceId: string): Promise<DocumentSpace> {
    try {
      return await sentryApiRequest<DocumentSpace>(`/spaces/${spaceId}`);
    } catch (error) {
      console.error('Failed to get space:', error);
      throw error;
    }
  }

  async updateSpace(spaceId: string, data: UpdateSpaceRequest): Promise<DocumentSpace> {
    try {
      return await sentryApiRequest<DocumentSpace>(`/spaces/${spaceId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to update space:', error);
      throw error;
    }
  }

  async deleteSpace(spaceId: string): Promise<{ success: boolean }> {
    try {
      return await sentryApiRequest<{ success: boolean }>(`/spaces/${spaceId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete space:', error);
      throw error;
    }
  }

  async uploadSpaceDocuments(spaceId: string, files: File[]): Promise<SpaceUploadDocumentsResponse> {
    const extensionToMimeType: Record<string, string> = {
      '.pdf': 'application/pdf',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.txt': 'text/plain',
      '.csv': 'text/csv',
      '.md': 'text/markdown',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.tiff': 'image/tiff',
      '.tif': 'image/tiff',
      '.bmp': 'image/bmp',
      '.msg': 'application/vnd.ms-outlook',
    };

    const getContentType = (file: File): string => {
      if (file.type) return file.type;
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return extensionToMimeType[ext] || 'application/octet-stream';
    };

    try {
      const filesMetadata = files.map((file) => ({
        fileName: file.name,
        fileSize: file.size,
        contentType: getContentType(file),
      }));

      const response = await sentryApiRequest<SpaceUploadDocumentsResponse>(`/spaces/${spaceId}/documents`, {
        method: 'POST',
        body: JSON.stringify({ files: filesMetadata }),
      });

      const uploadPromises = response.uploadUrls.map(async (uploadData, index) => {
        const file = files[index];
        const formData = new FormData();
        Object.entries(uploadData.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', file);

        const uploadResponse = await fetch(uploadData.uploadUrl, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed for ${file.name}: ${uploadResponse.statusText}`);
        }
        return uploadData.documentId;
      });

      await Promise.all(uploadPromises);
      return response;
    } catch (error) {
      console.error('Space document upload failed:', error);
      throw error;
    }
  }

  async listSpaceDocuments(spaceId: string): Promise<ListSpaceDocumentsResponse> {
    try {
      return await sentryApiRequest<ListSpaceDocumentsResponse>(`/spaces/${spaceId}/documents`);
    } catch (error) {
      console.error('Failed to list space documents:', error);
      throw error;
    }
  }

  async deleteSpaceDocuments(spaceId: string, documentIds: string[]): Promise<DeleteSpaceDocumentsResponse> {
    try {
      return await sentryApiRequest<DeleteSpaceDocumentsResponse>(`/spaces/${spaceId}/documents`, {
        method: 'DELETE',
        body: JSON.stringify({ documentIds }),
      });
    } catch (error) {
      console.error('Failed to delete space documents:', error);
      throw error;
    }
  }

  // ========================================================================
  // SPACE MEMBER OPERATIONS (Sharing)
  // ========================================================================

  async listSpaceMembers(spaceId: string): Promise<{ members: SpaceMember[]; totalCount: number }> {
    try {
      return await sentryApiRequest<{ members: SpaceMember[]; totalCount: number }>(`/spaces/${spaceId}/members`);
    } catch (error) {
      console.error('Failed to list space members:', error);
      throw error;
    }
  }

  async addSpaceMember(spaceId: string, data: { memberName: string; memberType?: string }): Promise<SpaceMember> {
    try {
      return await sentryApiRequest<SpaceMember>(`/spaces/${spaceId}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to add space member:', error);
      throw error;
    }
  }

  async removeSpaceMember(spaceId: string, memberId: string): Promise<{ success: boolean }> {
    try {
      return await sentryApiRequest<{ success: boolean }>(
        `/spaces/${spaceId}/members/${encodeURIComponent(memberId)}`,
        { method: 'DELETE' },
      );
    } catch (error) {
      console.error('Failed to remove space member:', error);
      throw error;
    }
  }

  // ========================================================================
  // IMPORT FROM SPACE
  // ========================================================================

  async importFromSpace(
    projectId: string,
    spaceId: string,
    documentIds?: string[],
    importGroups?: boolean,
  ): Promise<SpaceCopyJobResponse> {
    try {
      return await sentryApiRequest<SpaceCopyJobResponse>(`/projects/${projectId}/import-from-space`, {
        method: 'POST',
        body: JSON.stringify({ spaceId, documentIds, importGroups }),
      });
    } catch (error) {
      console.error('Failed to import from space:', error);
      throw error;
    }
  }

  // ========================================================================
  // SAVE TO SPACE
  // ========================================================================

  async saveToSpace(
    projectId: string,
    spaceId: string,
    documentIds: string[],
    includeGroups?: boolean,
  ): Promise<SpaceCopyJobResponse> {
    try {
      return await sentryApiRequest<SpaceCopyJobResponse>(`/projects/${projectId}/save-to-space`, {
        method: 'POST',
        body: JSON.stringify({ spaceId, documentIds, includeGroups }),
      });
    } catch (error) {
      console.error('Failed to save to space:', error);
      throw error;
    }
  }

  // ========================================================================
  // SPACE COPY JOB POLLING
  // ========================================================================

  async getSpaceCopyJobStatus(projectId: string, jobId: string): Promise<SpaceCopyJobResponse> {
    try {
      return await sentryApiRequest<SpaceCopyJobResponse>(`/projects/${projectId}/space-copy-jobs/${jobId}`);
    } catch (error) {
      console.error('Failed to get space copy job status:', error);
      throw error;
    }
  }

  // ========================================================================
  // DOCUMENT GROUP OPERATIONS (Project-scoped)
  // ========================================================================

  /**
   * Create a document group in a project
   */
  async createDocumentGroup(projectId: string, request: CreateDocumentGroupRequest): Promise<DocumentGroup> {
    try {
      return await sentryApiRequest<DocumentGroup>(`/projects/${projectId}/document-groups`, {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Failed to create document group:', error);
      throw error;
    }
  }

  /**
   * List document groups in a project
   */
  async listDocumentGroups(projectId: string): Promise<ListDocumentGroupsResponse> {
    try {
      return await sentryApiRequest<ListDocumentGroupsResponse>(`/projects/${projectId}/document-groups`);
    } catch (error) {
      console.error('Failed to list document groups:', error);
      throw error;
    }
  }

  /**
   * Get a specific document group in a project
   */
  async getDocumentGroup(projectId: string, groupId: string): Promise<DocumentGroup> {
    try {
      return await sentryApiRequest<DocumentGroup>(`/projects/${projectId}/document-groups/${groupId}`);
    } catch (error) {
      console.error('Failed to get document group:', error);
      throw error;
    }
  }

  /**
   * Update a document group in a project
   */
  async updateDocumentGroup(
    projectId: string,
    groupId: string,
    request: UpdateDocumentGroupRequest,
  ): Promise<DocumentGroup> {
    try {
      return await sentryApiRequest<DocumentGroup>(`/projects/${projectId}/document-groups/${groupId}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Failed to update document group:', error);
      throw error;
    }
  }

  /**
   * Delete a document group in a project (removes group only, not member documents)
   */
  async deleteDocumentGroup(projectId: string, groupId: string): Promise<void> {
    try {
      await sentryApiRequest<{ success: boolean }>(`/projects/${projectId}/document-groups/${groupId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete document group:', error);
      throw error;
    }
  }

  // ========================================================================
  // DOCUMENT GROUP OPERATIONS (Space-scoped)
  // ========================================================================

  /**
   * Create a document group in a space
   */
  async createSpaceDocumentGroup(spaceId: string, request: CreateDocumentGroupRequest): Promise<DocumentGroup> {
    try {
      return await sentryApiRequest<DocumentGroup>(`/spaces/${spaceId}/document-groups`, {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Failed to create space document group:', error);
      throw error;
    }
  }

  /**
   * List document groups in a space
   */
  async listSpaceDocumentGroups(spaceId: string): Promise<ListDocumentGroupsResponse> {
    try {
      return await sentryApiRequest<ListDocumentGroupsResponse>(`/spaces/${spaceId}/document-groups`);
    } catch (error) {
      console.error('Failed to list space document groups:', error);
      throw error;
    }
  }

  /**
   * Get a specific document group in a space
   */
  async getSpaceDocumentGroup(spaceId: string, groupId: string): Promise<DocumentGroup> {
    try {
      return await sentryApiRequest<DocumentGroup>(`/spaces/${spaceId}/document-groups/${groupId}`);
    } catch (error) {
      console.error('Failed to get space document group:', error);
      throw error;
    }
  }

  /**
   * Update a document group in a space
   */
  async updateSpaceDocumentGroup(
    spaceId: string,
    groupId: string,
    request: UpdateDocumentGroupRequest,
  ): Promise<DocumentGroup> {
    try {
      return await sentryApiRequest<DocumentGroup>(`/spaces/${spaceId}/document-groups/${groupId}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Failed to update space document group:', error);
      throw error;
    }
  }

  /**
   * Delete a document group in a space (removes group only, not member documents)
   */
  async deleteSpaceDocumentGroup(spaceId: string, groupId: string): Promise<void> {
    try {
      await sentryApiRequest<{ success: boolean }>(`/spaces/${spaceId}/document-groups/${groupId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete space document group:', error);
      throw error;
    }
  }

  // ========================================================================
  // ERROR HANDLING UTILITIES
  // ========================================================================

  /**
   * Check if response is an API error and handle appropriately
   */
  private handleApiError(response: any): never {
    if (isApiError(response)) {
      throw new Error(response.error.message);
    }
    throw new Error('Unknown API error');
  }

  /**
   * Safe API call with automatic error handling and type validation
   */
  private async safeApiCall<T>(
    apiCall: () => Promise<any>,
    validator: (data: any) => T,
    operationName: string,
  ): Promise<T> {
    try {
      const response = await apiCall();

      if (isApiError(response)) {
        this.handleApiError(response);
      }

      return validator(response);
    } catch (error) {
      if (error instanceof ValidationError) {
        console.error(`Type validation failed in ${operationName}:`, error.message);

        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Type mismatch in ${operationName}: ${error.message}`);
        }

        // In production, log the error but don't expose internal details
        throw new Error(`Data format error in ${operationName}`);
      }

      throw error;
    }
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const apiService = new ApiService();

// ============================================================================
// LEGACY COMPATIBILITY (for gradual migration)
// ============================================================================

/**
 * Legacy project API functions for backward compatibility
 * These will be deprecated once all components are migrated
 */
export const projectApi = {
  listProjects: (params?: { status?: string; search?: string; maxResults?: number }) => apiService.listProjects(params),

  createProject: (data: CreateProjectRequest) => apiService.createProject(data),

  getProject: (projectId: string) => apiService.getProject(projectId),

  deleteProject: (projectId: string) => apiService.deleteProject(projectId),

  duplicateProject: (projectId: string, data: DuplicateProjectRequest) => apiService.duplicateProject(projectId, data),
};

// ============================================================================
// TYPE ALIGNMENT MONITORING (Development Only)
// ============================================================================

if (process.env.NODE_ENV === 'development') {
  // Monitor API calls for type mismatches
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    // Log API responses for type alignment debugging
    if (args[0]?.toString().includes('/projects')) {
      const clonedResponse = response.clone();
      try {
        const data = await clonedResponse.json();
        console.log('🔍 API Response Debug:', {
          url: args[0],
          status: response.status,
          data: data,
        });
      } catch (e) {
        // Ignore JSON parsing errors
      }
    }

    return response;
  };
}
